import React, { RefObject } from 'react'
import { ScreenReaderOnly } from '@moonpig/launchpad-components'
import { LocaleTextHook } from '@moonpig/web-core-locale-text'
import type { SubmitItemType } from '../../types'
import { DepartmentType } from '../../departmentMap'
import { createHandleSubmit } from './createHandleSubmit'
import { ClearButton } from '../../ClearButton'
import { SubmitButton } from '../../SubmitButton'
import { NAV_TEXT } from '../../../locale'
import { Input } from '../../Input'
import { useGetPlaceholderText } from './useGetPlaceholderText'

type SearchControlsProps = {
  localiseText: LocaleTextHook<typeof NAV_TEXT>
  inputValue: string
  isOpen: boolean
  searchInput: RefObject<HTMLInputElement | null>
  onClear: () => void
  clearButtonAs: (({ value }: { value: string }) => React.ReactNode) | undefined
  activeDepartment: DepartmentType
  handleInputFocus: (searchOpen: boolean) => void
  closeSearch: () => void
  items: Array<{ department: { enum: string } }>
  onSubmit: (submitItem: SubmitItemType) => void
  onKeyUp: (value: string) => void
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  isMobile: boolean
  searchDepartment: {
    name: string
    title: string
  }
}

export const SearchControls = ({
  localiseText,
  inputValue,
  isOpen,
  searchInput,
  onClear,
  clearButtonAs,
  items,
  onSubmit,
  onKeyUp,
  onKeyDown,
  activeDepartment,
  handleInputFocus,
  closeSearch,
  searchDepartment,
  isMobile,
}: SearchControlsProps) => {
  const placeholderText = useGetPlaceholderText(isMobile)

  const handleClearClick = () => {
    if (onClear) {
      onClear()
    }
    if (searchInput.current) {
      searchInput.current.focus()
    }
    closeSearch()
  }

  const handleSubmit = createHandleSubmit({
    searchInput,
    activeDepartment,
    items,
    onSubmit,
    handleInputFocus,
    closeSearch,
    searchDepartment,
  })

  return (
    <>
      <ScreenReaderOnly as="label">
        {localiseText('search.search')}
      </ScreenReaderOnly>

      <Input
        ref={searchInput}
        placeholder={placeholderText}
        type="search"
        data-testid="search-input"
        isSearchOpen={isOpen}
        value={inputValue}
        onFocus={() => handleInputFocus(true)}
        onKeyUp={e => onKeyUp(e.currentTarget.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault()
            handleSubmit({
              itemType: 'search',
              value: inputValue,
            })
          }
          onKeyDown(e)
        }}
      />
      <ClearButton
        hidden={!inputValue}
        onClick={eve => {
          if (isOpen) {
            eve.stopPropagation()
          }
          handleClearClick()
        }}
        as={
          (clearButtonAs && clearButtonAs({ value: inputValue || '' })) as
            | keyof React.JSX.IntrinsicElements
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            | React.ComponentType<React.PropsWithChildren<any>>
        }
        isSearchOpen={isOpen}
      />
      <SubmitButton
        disabled={!inputValue}
        onClick={() => {
          handleSubmit({
            itemType: 'search',
            value: inputValue,
          })
          onClear()
        }}
        isSearchOpen={isOpen}
      />
    </>
  )
}
